import {
    formatNumber,
    formatProfilingTimestamp,
    formatScalar,
    type FieldStatsView,
} from "./dataProfiling.utils";

interface FieldProfileDetailProps {
    view: FieldStatsView;
}

const COLOR_DISTINCT = "#0F2F64";
const COLOR_NON_DISTINCT = "#A6C4F2";
const COLOR_NULL = "#E0E0E0";

interface RowProps {
    label: string;
    color: string;
    count: number;
    percent: number;
}

function StatRow({ label, color, count, percent }: RowProps) {
    return (
        <div className="grid grid-cols-[16px_1fr_120px_80px_minmax(180px,1fr)] items-center gap-3 py-1">
            <span className="size-3 rounded-sm" style={{ backgroundColor: color }} aria-hidden />
            <span className="text-sm text-foreground">{label}</span>
            <span className="text-sm text-foreground tabular-nums">{formatNumber(count, 0)}</span>
            <span className="text-sm text-muted-foreground tabular-nums">
                {percent.toFixed(1)}%
            </span>
            <div className="h-2 rounded bg-muted overflow-hidden">
                <div
                    className="h-full rounded"
                    style={{
                        width: `${Math.min(100, Math.max(0, percent))}%`,
                        backgroundColor: color,
                    }}
                />
            </div>
        </div>
    );
}

function StackedBar({
    distinctPercent,
    duplicatePercent,
    nullPercent,
}: {
    distinctPercent: number;
    duplicatePercent: number;
    nullPercent: number;
}) {
    const ticks = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    return (
        <div className="space-y-2">
            <div className="flex h-3 w-full overflow-hidden rounded">
                <div style={{ width: `${distinctPercent}%`, backgroundColor: COLOR_DISTINCT }} />
                <div style={{ width: `${duplicatePercent}%`, backgroundColor: COLOR_NON_DISTINCT }} />
                <div style={{ width: `${nullPercent}%`, backgroundColor: COLOR_NULL }} />
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground tabular-nums">
                {ticks.map((tick) => (
                    <span key={tick}>{tick}%</span>
                ))}
            </div>
            <div className="flex items-center gap-4 text-xs text-foreground">
                <LegendDot color={COLOR_DISTINCT} label={`Distinct values (${distinctPercent.toFixed(1)}%)`} />
                <LegendDot color={COLOR_NON_DISTINCT} label={`Non Distinct values (${duplicatePercent.toFixed(1)}%)`} />
            </div>
        </div>
    );
}

function LegendDot({ color, label }: { color: string; label: string }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className="size-2.5 rounded-sm" style={{ backgroundColor: color }} aria-hidden />
            {label}
        </span>
    );
}

function MinMeanMaxBar({
    min,
    mean,
    max,
}: {
    min: number;
    mean: number;
    max: number;
}) {
    const safeMax = Number.isFinite(max) ? max : 0;
    const safeMin = Number.isFinite(min) ? min : 0;
    const range = safeMax - safeMin;
    const meanPct = range > 0 ? ((mean - safeMin) / range) * 100 : 50;

    return (
        <div className="relative pt-6 pb-12">
            <div className="h-1.5 w-full rounded-full bg-muted" />
            <Marker leftPct={0} value={safeMin} label="Min" />
            <Marker leftPct={Math.min(100, Math.max(0, meanPct))} value={mean} label="Mean" />
            <Marker leftPct={100} value={safeMax} label="Max" />
        </div>
    );
}

function Marker({ leftPct, value, label }: { leftPct: number; value: number; label: string }) {
    return (
        <div
            className="absolute -translate-x-1/2 flex flex-col items-center"
            style={{ left: `${leftPct}%`, top: "16px" }}
        >
            <span className="block h-4 w-px bg-foreground" />
            <span className="mt-1 text-xs font-medium tabular-nums">{formatNumber(value, 2)}</span>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
        </div>
    );
}

export default function FieldProfileDetail({ view }: FieldProfileDetailProps) {
    const showNumericVisuals = view.isNumeric;
    const minNum = Number(view.minValue);
    const maxNum = Number(view.maxValue);
    const meanNum = Number(view.mean);
    const canRenderRangeBar =
        showNumericVisuals &&
        Number.isFinite(minNum) &&
        Number.isFinite(maxNum) &&
        Number.isFinite(meanNum);

    return (
        <div className="flex flex-col gap-6">
            <div>
                <h3 className="text-lg font-semibold text-foreground">{view.fieldName}</h3>
                {view.createdTimestamp && (
                    <p className="text-sm text-muted-foreground">
                        {formatProfilingTimestamp(view.createdTimestamp)}
                    </p>
                )}
            </div>

            <section className="space-y-3">
                <h4 className="text-sm font-semibold text-foreground">Rows</h4>
                <div className="space-y-1">
                    <StatRow
                        label="Distinct values"
                        color={COLOR_DISTINCT}
                        count={view.distinctCount}
                        percent={view.distinctPercent}
                    />
                    <StatRow
                        label="Non distinct values"
                        color={COLOR_NON_DISTINCT}
                        count={view.duplicate}
                        percent={view.duplicatePercent}
                    />
                    <StatRow
                        label="Null values"
                        color={COLOR_NULL}
                        count={view.nullValue}
                        percent={view.nullPercent}
                    />
                </div>
                <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
                    <span className="text-sm font-semibold">Total Rows</span>
                    <span className="text-sm font-semibold tabular-nums">
                        {formatNumber(view.totalRows, 0)}
                    </span>
                </div>
                <StackedBar
                    distinctPercent={view.distinctPercent}
                    duplicatePercent={view.duplicatePercent}
                    nullPercent={view.nullPercent}
                />
            </section>

            {showNumericVisuals && (
                <section className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
                    <div>
                        <h4 className="text-sm font-semibold text-foreground mb-3">Values</h4>
                        <dl className="space-y-2">
                            <KeyValueRow label="Min" value={formatScalar(view.minValue)} />
                            <KeyValueRow label="Max" value={formatScalar(view.maxValue)} />
                            <KeyValueRow
                                label="Distinct Values"
                                value={formatNumber(view.distinctCount, 0)}
                            />
                        </dl>
                    </div>
                    <div>
                        <h4 className="text-sm font-semibold text-foreground mb-3">Statistics</h4>
                        <dl className="space-y-2">
                            <KeyValueRow label="Mean" value={formatNumber(view.mean ?? null, 2)} />
                            <KeyValueRow
                                label="Standard deviation"
                                value={formatNumber(view.standardDeviation ?? null, 2)}
                            />
                            <KeyValueRow label="Median" value={formatNumber(view.median ?? null, 2)} />
                        </dl>
                    </div>
                </section>
            )}

            {canRenderRangeBar && (
                <section>
                    <MinMeanMaxBar min={minNum} mean={meanNum} max={maxNum} />
                </section>
            )}
        </div>
    );
}

function KeyValueRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-center justify-between border-b border-dashed border-muted py-1.5">
            <dt className="text-sm text-muted-foreground">{label}</dt>
            <dd className="text-sm font-medium tabular-nums">{value}</dd>
        </div>
    );
}
